// src/pages/StudyPage.js - 아티팩트 상세 페이지

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faSave,
  faCopy,
  faDownload,
  faTimes,
  faFileCode,
  faQuestionCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table as TiptapTable } from "@tiptap/extension-table";
import { TableRow as TiptapTableRow } from "@tiptap/extension-table-row";
import { TableCell as TiptapTableCell } from "@tiptap/extension-table-cell";
import { TableHeader as TiptapTableHeader } from "@tiptap/extension-table-header";
import {
  AlignmentType,
  BorderStyle,
} from "docx";
import WorkspaceLayout from "../components/WorkspaceLayout";
import B2BLayout from "../components/b2b/B2BLayout"; // [B2B] B2B 레이아웃
import "./DetailPageHeader.scss"; // detail-page-content/detail-content-card 공용 레이아웃 스타일
import "./StudyPage.scss";
import "./TiptapEditor.scss";
import "./GuidelineMarkdown.scss";
import { faTrash } from "@fortawesome/free-solid-svg-icons"; // 삭제 아이콘
import { useArtifactData } from '../hooks/useArtifactData';
import { usePageHeader } from "../context/PageHeaderContext";
import {
  getArtifactUrl,
  getStudyUrl,
  getArtifactDeletedToastMessage,
  getArtifactTitle,
  parseSurveyData,
  markdownToHtml
} from '../utils/artifactUtils';
import ArtifactToolbox from '../components/artifacts/ArtifactToolbox';
import ArtifactModals from '../components/artifacts/ArtifactModals';
import { Toast } from '../components/ui';
import FeedbackButton from '../components/FeedbackButton';
import StudyArtifactContentCardBody from "../features/study/components/StudyArtifactContentCardBody";
import { useArtifactExportActions } from "../features/study/hooks/useArtifactExportActions";
import { useArtifactSaveActions } from "../features/study/hooks/useArtifactSaveActions";
import { useArtifactDeleteActions } from "../features/study/hooks/useArtifactDeleteActions";
import { useB2BMode } from "../hooks/useB2BMode";

const StudyPage = () => {
  const { studySlug, artifactId } = useParams(); // slug로 변경
  const navigate = useNavigate();
  const location = useLocation();
  
  // [B2B] B2B 모드 체크
  const { isB2BMode } = useB2BMode();
  const Layout = isB2BMode ? B2BLayout : WorkspaceLayout;
  
  console.log('StudyPage 렌더링 - studySlug:', studySlug, 'artifactId:', artifactId);
  
  // ✨ 커스텀 훅으로 데이터 로딩
  const { 
    study, 
    studyId, 
    artifact, 
    loading, 
    setArtifact, 
    streamingContent, 
    isGenerating,
    studyStatus,
    artifactStatus,
    retry,
  } = useArtifactData(studySlug, artifactId);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);

  const titleFromTypeParam = React.useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const t = (params.get("type") || "").toLowerCase();
      if (t === "plan") return "리서치 계획서";
      if (t === "survey") return "스크리닝 설문";
      if (t === "guideline") return "진행 가이드라인";
      return "";
    } catch (e) {
      return "";
    }
  }, [location.search]);

  usePageHeader(
    {
      title: getArtifactTitle(artifact?.artifact_type) || titleFromTypeParam || "문서",
      subtitle: study?.name || "",
      showBack: true,
      onBack: () => navigate(`/study/${studySlug}`),
    },
    [artifact?.artifact_type, titleFromTypeParam, study?.name, studySlug]
  );

  const {
    deleteConfirmModal,
    openDeleteConfirm,
    cancelDeleteConfirm,
    confirmDelete,
  } = useArtifactDeleteActions({
    artifactId,
    onDeleteSuccess: () => {
      navigate(`/study/${studySlug}`, {
        state: {
          toast: {
            message: getArtifactDeletedToastMessage(artifact?.artifact_type),
            type: 'success',
          },
        },
      });
    },
  });

  const [toast, setToast] = useState({ isOpen: false, type: 'success', message: '' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ isOpen: true, type, message });
  }, []);

  // 구글 폼 생성 안내 모달 상태
  const [googleFormsModal, setGoogleFormsModal] = useState({
    isOpen: false,
    script: '',
  });

  // 배포 모달 상태
  const [distributeModal, setDistributeModal] = useState({
    isOpen: false,
    surveyData: null
  });

  // 아티팩트 타입별 생성 단계
  const getGenerationSteps = (artifactType) => {
    switch (artifactType) {
      case "plan":
        return [
          "리서치 전문가가 상황을 분석중입니다.",
          "리서치 전문가가 적합한 조사 방법을 찾고있습니다.",
          "리서치 전문가가 조사 대상자를 결정하고 있습니다.",
          "리서치 전문가가 조사 일정을 구체화하고 있습니다.",
          "최종 계획서를 작성중입니다.",
        ];
      case "survey":
        return [
          "계획서에서 핵심 변수를 추출하고 있습니다.",
          "타겟 그룹을 분석하고 있습니다.",
          "설문 문항을 생성하고 있습니다.",
        ];
      case "guideline":
        return [
          "리서치 계획서를 분석 중 입니다.",
          "관련 가이드라인 자료를 검색하고 있습니다.",
          "진행 가이드를 작성하고 있습니다.",
        ];
      default:
        return ["생성 중입니다..."];
    }
  };

  // TipTap 에디터 초기화
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapTable.configure({
        resizable: true,
      }),
      TiptapTableRow,
      TiptapTableHeader,
      TiptapTableCell,
    ],
    content: "",
    editable: false,
  });

  // ✅ Hooks must be called unconditionally.
  // loading/!artifact 상태에서도 훅이 안전하게 동작하도록 fallback 객체를 사용한다.
  const safeArtifact = artifact || { artifact_type: "", content: "" };
  const safeStudyName = study?.name || "";

  const { handleSave, handleSaveSurveyJS } = useArtifactSaveActions({
    artifact: safeArtifact,
    artifactId,
    editor,
    editedContent,
    parseSurveyData,
  });

  const {
    handleCopy,
    handleDownloadWord,
    handleDownloadPDF,
    handleDownloadMarkdown,
    handleGenerateGoogleFormsScript,
  } = useArtifactExportActions({
    artifact: safeArtifact,
    studyName: safeStudyName,
    parseSurveyData,
    onCopySuccess: () => showToast('클립보드에 복사되었습니다.'),
    setGoogleFormsModal,
  });

  const handleShare = React.useCallback(async () => {
    try {
      const url = window.location.href;
      await navigator.clipboard.writeText(url);
      alert("공유 링크를 복사했습니다.");
    } catch (e) {
      alert("공유 링크 복사에 실패했습니다.");
    }
  }, []);

  const handleCancelEdit = React.useCallback(() => {
    setIsEditing(false);
    try {
      if (editor) {
        editor.setEditable(false);
      }
    } catch (e) {
      // ignore
    }
  }, [editor]);

  // 생성 중일 때 단계별 진행
  useEffect(() => {
    if (!artifact || artifact.status !== "pending") return;

    const steps = getGenerationSteps(artifact.artifact_type);
    const stepDuration =
      artifact.artifact_type === "plan"
        ? 7000
        : artifact.artifact_type === "survey"
        ? 10000
        : 10000; // guideline

    if (generationStep < steps.length - 1) {
      const timer = setTimeout(() => {
        setGenerationStep((prev) => prev + 1);
      }, stepDuration);

      return () => clearTimeout(timer);
    }
  }, [generationStep, artifact]);

  // Plan/Guideline 읽기 화면에서도 Tiptap을 "뷰어"로 사용하기 위해
  // - plan/guideline이면 artifact.content를 HTML로 변환해 editor에 로드
  // - 기본은 locked(editable=false)
  useEffect(() => {
    if (!editor) return;
    if (!artifact) return;
    if (artifact.artifact_type !== "plan" && artifact.artifact_type !== "guideline") return;

    // 직접 편집 모드가 아닐 때(=기본 locked), 뷰어용으로 content를 동기화한다.
    // (편집 모드에서는 기존 flow(handleEdit/handleDirectEdit)가 setContent를 수행)
    if (!isEditing) {
      try {
        const htmlContent = markdownToHtml(artifact.content || "");
        editor.commands.setContent(htmlContent);
        editor.setEditable(false);
      } catch (e) {
        // ignore: 변환/로드 실패 시 기존 content 유지
        console.warn("Viewer editor load failed:", e);
      }
    }
  }, [editor, artifact?.artifact_type, artifact?.content, isEditing]);

  // ✨ 데이터 로딩 로직은 useArtifactData 훅으로 이동됨

  if (loading) {
    return (
      <Layout hideCollapseButton={false} defaultCollapsed={true}>
        <div style={{ padding: "40px", textAlign: "center" }}>로딩 중...</div>
      </Layout>
    );
  }

  // "없음"을 너무 빨리 단정하지 않도록:
  // - study/artifact가 not_found로 확정된 경우에만 "찾을 수 없음"을 보여준다.
  // - 그 외(error/unknown)는 재시도/로딩 UX로 처리한다.
  const isNotFound = studyStatus === "not_found" || artifactStatus === "not_found";
  const isSoftLoading = !isNotFound && (!study || !artifact);

  if (isSoftLoading) {
    return (
      <Layout hideCollapseButton={false} defaultCollapsed={true}>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p style={{ marginBottom: 12 }}>아티팩트를 불러오는 중입니다…</p>
          <button
            type="button"
            onClick={() => retry?.()}
            style={{ marginRight: 10 }}
          >
            다시 시도
          </button>
          <button type="button" onClick={() => window.location.reload()}>
            새로고침
          </button>
        </div>
      </Layout>
    );
  }

  if (isNotFound || !study || !artifact) {
    return (
      <Layout hideCollapseButton={false} defaultCollapsed={true}>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>페이지를 찾을 수 없습니다.</p>
          <p style={{ color: "#6b7280", fontSize: 14, marginTop: 8 }}>
            삭제되었거나 접근 권한이 없을 수 있습니다.
          </p>
          <button onClick={() => navigate(isB2BMode ? '/research' : -1)}>뒤로 가기</button>
        </div>
      </Layout>
    );
  }


  // 수정 시작
  const handleEdit = () => {
    // Survey 타입은 항상 편집 가능하므로 수정 모드로 전환할 필요 없음
    if (artifact.artifact_type === "survey") {
      return;
    }
    
    if (editor) {
      // 마크다운을 HTML로 변환하여 에디터에 로드
      const htmlContent = markdownToHtml(artifact.content);
      editor.commands.setContent(htmlContent);
      editor.setEditable(true);
    }
    setEditedContent(artifact.content);
    setIsEditing(true);
  };

  // 직접 수정 모드로 전환 (선택된 텍스트 위치로 커서 이동)
  const handleDirectEdit = (selectedText, selectionPos) => {
    // Survey 타입은 처리하지 않음
    if (artifact.artifact_type === "survey") {
      return;
    }

    // ProseMirror doc에서 특정 텍스트의 from/to position 찾기
    const findTextSelectionInDoc = (doc, needle) => {
      if (!doc || !needle) return null;
      const target = needle.trim();
      if (!target) return null;

      // doc.descendants로 텍스트를 순회하며 가장 첫 매칭을 찾음
      let found = null;
      doc.descendants((node, pos) => {
        if (found) return false;
        if (!node.isText || !node.text) return true;
        const idx = node.text.indexOf(target);
        if (idx !== -1) {
          // pos는 해당 text node 앞의 position
          const from = pos + idx;
          const to = from + target.length;
          found = { from, to };
          return false;
        }
        return true;
      });
      return found;
    };

    if (editor) {
      // 0순위: 드래그 당시 좌표(from/to)가 있으면, 그 위치로 바로 포커싱
      const hasPos =
        selectionPos &&
        typeof selectionPos.from === "number" &&
        typeof selectionPos.to === "number" &&
        selectionPos.from !== selectionPos.to;

      // plan 뷰어는 이미 editor에 content가 로드되어 있으므로(locked 상태),
      // 좌표 기반 direct edit일 때는 setContent를 다시 하지 않는다(좌표가 틀어질 수 있음).
      editor.setEditable(true);
      setIsEditing(true);

      setTimeout(() => {
        try {
          if (hasPos) {
            editor.commands.setTextSelection({ from: selectionPos.from, to: selectionPos.to });
            editor.commands.focus();
            const coords = editor.view.coordsAtPos(selectionPos.from);
            const targetTop = Math.max(0, coords.top + window.scrollY - 180);
            window.scrollTo({ top: targetTop, behavior: "smooth" });
            return;
          }

          // fallback: 텍스트로 재검색(기존 방식)
          const htmlContent = markdownToHtml(artifact.content);
          editor.commands.setContent(htmlContent);
          const found = findTextSelectionInDoc(editor.state.doc, selectedText);
          if (found) {
            editor.commands.setTextSelection(found);
            editor.commands.focus();
            const coords = editor.view.coordsAtPos(found.from);
            const targetTop = Math.max(0, coords.top + window.scrollY - 180);
            window.scrollTo({ top: targetTop, behavior: "smooth" });
          } else {
            editor.commands.focus();
          }
        } catch (error) {
          console.warn("커서 이동 실패, 에디터만 포커스:", error);
          editor.commands.focus();
        }
      }, 50);
    }
    
    setEditedContent(artifact.content);
    if (!editor) setIsEditing(true);
  };

  // 배포 페이지로 이동 (B2B 모드에서만)
  const handleOpenDistributeModal = (surveyData) => {
    if (isB2BMode) {
      // B2B 모드: 배포 페이지로 이동
      navigate(`/study/${studySlug}/survey/deploy`);
    } else {
      // B2C 모드: 준비 중인 기능 알림
      alert('준비 중인 기능입니다.');
    }
  };

  // 맨 위로
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isPlanArtifact = artifact?.artifact_type === "plan";
  const isSurveyArtifact = artifact?.artifact_type === "survey";
  const isGuidelineArtifact = artifact?.artifact_type === "guideline";
  const shouldHideToolbox = isPlanArtifact || isSurveyArtifact || isGuidelineArtifact;
  const shouldUseWideLayout = isPlanArtifact || isSurveyArtifact || isGuidelineArtifact;

  return (
    <Layout hideCollapseButton={false} defaultCollapsed={true}>
      {/* 아티팩트 내용 */}
      <main className={`detail-page-content ${shouldUseWideLayout ? "detail-page-content--plan" : ""}`}>
        <div className={`detail-content-card ${shouldUseWideLayout ? "detail-content-card--plan" : ""}`}>
          <StudyArtifactContentCardBody
            artifact={artifact}
            studyId={studyId}
            studyName={study?.name || ""}
            isGenerating={isGenerating}
            generationStep={generationStep}
            getGenerationSteps={getGenerationSteps}
            isEditing={isEditing}
            editor={editor}
            parseSurveyData={parseSurveyData}
            handleSaveSurveyJS={handleSaveSurveyJS}
            handleOpenDistributeModal={handleOpenDistributeModal}
            handleSave={handleSave}
            handleCancelEdit={handleCancelEdit}
            handleCopy={handleCopy}
            handleDownloadWord={handleDownloadWord}
            handleDownloadPDF={handleDownloadPDF}
            handleDownloadMarkdown={handleDownloadMarkdown}
            handleShare={handleShare}
            handleDelete={openDeleteConfirm}
            onArtifactUpdate={(updatedContent) => {
              setArtifact({ ...artifact, content: updatedContent });
            }}
            onDirectEdit={handleDirectEdit}
          />
        </div>

        {/* Feedback 버튼 - detail-content-card 밖에 위치 */}
        {!isEditing && !isGenerating && artifact.status === "completed" && (
          <div style={{ marginTop: "24px", width: "100%" }}>
            {artifact.artifact_type === "survey" && (
              <FeedbackButton category="screener" studyId={studyId} studyName={study?.name || ''} />
            )}
            {artifact.artifact_type === "plan" && (
              <FeedbackButton category="plan" studyId={studyId} studyName={study?.name || ''} />
            )}
            {artifact.artifact_type === "guideline" && (
              <FeedbackButton category="guide" studyId={studyId} studyName={study?.name || ''} />
            )}
          </div>
        )}

        {/* 플로팅 도구 박스 - sticky로 스크롤 시 따라다님 */}
        {!shouldHideToolbox && (
          <ArtifactToolbox
            isEditing={isEditing}
            showDownloadMenu={showDownloadMenu}
            artifactType={artifact.artifact_type}
            onScrollToTop={scrollToTop}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={() => setIsEditing(false)}
            onCopy={handleCopy}
            onDownloadWord={handleDownloadWord}
            onDownloadPDF={handleDownloadPDF}
            onGenerateGoogleFormsScript={handleGenerateGoogleFormsScript}
            onDelete={openDeleteConfirm}
            setShowDownloadMenu={setShowDownloadMenu}
          />
        )}

        {/* 모달들 */}
        <ArtifactModals
          artifact={artifact}
          studySlug={studySlug}
          studyId={studyId}
          artifactId={artifactId}
          deleteConfirmModal={deleteConfirmModal}
          googleFormsModal={googleFormsModal}
          distributeModal={distributeModal}
          onDeleteConfirm={confirmDelete}
          onDeleteCancel={cancelDeleteConfirm}
          onGoogleFormsModalClose={() => setGoogleFormsModal({ isOpen: false, script: '' })}
          onGoogleFormsScriptCopy={() => {
            navigator.clipboard.writeText(googleFormsModal.script);
            showToast('스크립트가 클립보드에 복사되었습니다.');
          }}
          onDistributeModalClose={() => setDistributeModal({ isOpen: false, surveyData: null })}
        />
        <Toast
          isOpen={toast.isOpen}
          type={toast.type}
          message={toast.message}
          onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
        />
        </main>
    </Layout>
  );
};

export default StudyPage;
